// "Você quis dizer joao@gmail.com?" embaixo de qualquer campo de e-mail do PMS.
//
// Um listener delegado no documento cobre os formulários clássicos, os modais montados depois e os
// de React, sem tocar em cada tela. O aviso aparece ao sair do campo (não durante a digitação) e
// some quando o valor muda. Clicar na sugestão troca o valor pelo setter nativo e dispara input e
// change, que é o que o React e o jquery-validation escutam.
//
// Um campo (ou um bloco inteiro) sai com data-email-typo-hint="off". O login usa isso: quem tem o
// cadastro com o domínio errado entra com ele, e a sugestão o levaria a um endereço sem conta.
import { suggestEmail } from "./email-typo";

export const EMAIL_INPUT_SELECTOR = 'input[type="email"], input[name="email"], input[name$="[email]"]';
export const HINT_CLASS = "email-typo-hint";

const MESSAGES: Record<string, { before: string; after: string }> = {
  pt: { before: "Você quis dizer ", after: "?" },
  en: { before: "Did you mean ", after: "?" },
  es: { before: "¿Quisiste decir ", after: "?" },
};

const hints = new WeakMap<HTMLInputElement, HTMLElement>();

function messages(doc: Document) {
  const lang = (doc.documentElement.lang || "pt").slice(0, 2).toLowerCase();
  return MESSAGES[lang] ?? MESSAGES.pt;
}

// Campo que a pessoa não edita (o e-mail escondido do formulário de nova senha, só para o
// gerenciador de senhas) não ganha aviso.
function isEmailInput(target: EventTarget | null): target is HTMLInputElement {
  return (
    target instanceof HTMLInputElement &&
    target.matches(EMAIL_INPUT_SELECTOR) &&
    !target.readOnly &&
    !target.disabled &&
    !target.closest('[hidden], [data-email-typo-hint="off"]')
  );
}

function setNativeValue(input: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  if (setter) setter.call(input, value);
  else input.value = value;
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
}

export function hideHint(input: HTMLInputElement) {
  hints.get(input)?.remove();
  hints.delete(input);
}

export function showHint(input: HTMLInputElement) {
  hideHint(input);
  const suggestion = suggestEmail(input.value);
  if (!suggestion) return;

  const doc = input.ownerDocument;
  const text = messages(doc);
  const hint = doc.createElement("small");
  hint.className = `${HINT_CLASS} d-block text-warning`;
  hint.setAttribute("role", "status");

  const button = doc.createElement("button");
  button.type = "button";
  button.className = "btn btn-link p-0 align-baseline";
  button.style.fontSize = "inherit";
  button.textContent = suggestion;
  button.addEventListener("click", () => {
    hideHint(input);
    setNativeValue(input, suggestion);
    input.focus();
  });

  hint.append(text.before, button, text.after);

  // Num input-group o aviso dentro do grupo quebraria o alinhamento do addon.
  const anchor = input.parentElement?.classList.contains("input-group") ? input.parentElement : input;
  anchor.insertAdjacentElement("afterend", hint);
  hints.set(input, hint);
}

export function installEmailTypoHint(doc: Document = document): () => void {
  const onFocusOut = (event: Event) => {
    if (isEmailInput(event.target)) showHint(event.target);
  };
  const onInput = (event: Event) => {
    if (isEmailInput(event.target)) hideHint(event.target);
  };

  doc.addEventListener("focusout", onFocusOut);
  doc.addEventListener("input", onInput);

  // Cadastro aberto já com o domínio errado (editar hóspede, meu perfil) avisa sem esperar o foco.
  for (const input of doc.querySelectorAll<HTMLInputElement>(EMAIL_INPUT_SELECTOR)) {
    if (isEmailInput(input) && input.value) showHint(input);
  }

  return () => {
    doc.removeEventListener("focusout", onFocusOut);
    doc.removeEventListener("input", onInput);
    for (const hint of doc.querySelectorAll(`.${HINT_CLASS}`)) hint.remove();
  };
}
